/**
 * Scoring for the scalar answer elements (rating, slider).
 *
 * A response-set element scores through its responses; a rating or slider
 * has no responses, so its number IS the score. A designer opts an element
 * in with `data.scorable`. The element's ceiling is its top value (a
 * rating's `max`, a slider's `max`) unless `data.scorePoints` pins the
 * points for the top value, in which case the answer is scaled onto that
 * range so a 0-100 slider can be worth the same as a 5-star rating.
 * `data.flagBelow` flags any answer under that number.
 *
 * Mirrored by FormDocumentResultService::scalarElementScoring in ISM,
 * which is the scorer of record; keep the two in step.
 */

export const SCALAR_SCORING_TYPE_IDS = new Set(["rating", "slider"]);

export interface ScalarScoringConfig {
  /** Lowest answer the control can give (0 for a rating, `min` for a slider). */
  low: number;
  /** Highest answer the control can give. */
  high: number;
  /** The element's contribution to the maximum score. */
  max: number;
  /** Answers strictly below this are flagged; null when the designer set none. */
  flagBelow: number | null;
}

export interface ScalarScore {
  /** What the answer is worth, or null when unanswered. */
  score: number | null;
  /** The element's contribution to the maximum score. */
  max: number;
  flagged: boolean;
}

function finite(v: unknown): number | null {
  if (v === null || v === undefined || v === "" || typeof v === "boolean") return null;
  const n = typeof v === "number" ? v : Number(String(v).trim());
  return Number.isFinite(n) ? n : null;
}

/** The scoring config for a rating or slider, or null when it is not scorable. */
export function scalarScoringConfig(
  typeId: string,
  data: Record<string, unknown> | undefined,
): ScalarScoringConfig | null {
  if (!SCALAR_SCORING_TYPE_IDS.has(typeId) || !data || !data.scorable) return null;
  let low = 0;
  let high: number | null;
  if (typeId === "rating") {
    high = finite(data.max) ?? 5;
  } else {
    low = finite(data.min) ?? 0;
    high = finite(data.max) ?? 100;
  }
  if (high <= low) return null;
  const points = finite(data.scorePoints);
  const max = points !== null && points > 0 ? points : high;
  return { low, high, max, flagBelow: finite(data.flagBelow) };
}

/**
 * Score one answer (the unwrapped value) against the element's config.
 * Unanswered → score null; the element still contributes its max, as an
 * unanswered scoring question does in a response set.
 */
export function scoreScalarAnswer(config: ScalarScoringConfig, value: unknown): ScalarScore {
  const n = finite(value);
  if (n === null) return { score: null, max: config.max, flagged: false };
  const clamped = Math.min(config.high, Math.max(config.low, n));
  const score =
    config.max === config.high
      ? clamped
      : ((clamped - config.low) / (config.high - config.low)) * config.max;
  const flagged = config.flagBelow !== null && n < config.flagBelow;
  return { score, max: config.max, flagged };
}
